import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import planoService from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';

const Field: React.FC<{ label: string; value?: React.ReactNode; className?: string }> = ({ label, value, className = '' }) => (
  <div className={`rounded-md border border-slate-200 px-3 py-3 ${className}`}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
    <div className="mt-1 text-[13px] leading-5 text-slate-900">{value ?? 'Nao informado'}</div>
  </div>
);

const SectionTitle: React.FC<{ number: string; title: string }> = ({ number, title }) => (
  <div className="mb-3 mt-8 border-b border-slate-300 pb-2 print-keep-with-next">
    <h2 className="text-base font-bold uppercase tracking-[0.12em] text-slate-900">{number} - {title}</h2>
  </div>
);

const ParecerGestorPrintPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plano, setPlano] = useState<PlanoTrabalho | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Plano nao informado.');
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        // getFull expõe parecerTecnicoHash/InseridoPor/InseridoEm e gestorSecretaria,
        // que o DTO simples de get() não retorna.
        const planoRes = await planoService.getFull(id);
        setPlano(planoRes.data as PlanoTrabalho);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Nao foi possivel carregar o impresso do parecer.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  useEffect(() => {
    if (!loading && !error && plano?.parecerTecnico && searchParams.get('autoprint') === '1') {
      const timeout = window.setTimeout(() => window.print(), 400);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [loading, error, plano, searchParams]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 px-4 py-10 text-center text-slate-600">Carregando impresso do parecer...</div>;
  }

  if (error || !plano) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-red-700">Erro ao gerar impresso</p>
          <p className="mt-2 text-sm text-slate-600">{error || 'Plano de trabalho nao encontrado.'}</p>
          <button type="button" onClick={() => navigate(-1)} className="mt-5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const semParecer = !plano.parecerTecnico || !plano.parecerTecnico.trim();
  const inseridoEm = plano.parecerTecnicoInseridoEm
    ? new Date(plano.parecerTecnicoInseridoEm).toLocaleString('pt-BR')
    : 'Nao informado';

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <style>{`
        @page { size: A4 portrait; margin: 14mm; }
        @media print {
          .print-hidden { display: none !important; }
          .print-sheet { box-shadow: none !important; margin: 0 !important; max-width: none !important; }
          .print-keep-together {
            break-inside: avoid-page !important;
            page-break-inside: avoid !important;
          }
          .print-keep-with-next {
            break-after: avoid-page !important;
            page-break-after: avoid !important;
          }
          body { background: #fff !important; }
        }
      `}</style>

      <div className="print-hidden sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Impresso do Parecer do Gestor da Parceria</p>
            <p className="text-xs text-slate-500">{plano.titulo || 'Plano de trabalho'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate(-1)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700">
              Voltar
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              disabled={semParecer}
              className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              Imprimir
            </button>
          </div>
        </div>
      </div>

      <main className="print-sheet mx-auto my-6 max-w-5xl rounded-3xl bg-white px-6 py-8 shadow-xl print:my-0 print:rounded-none print:px-0 print:py-0">
        <header className="border-b border-slate-300 pb-5">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Gestor da Parceria</p>
          <h1 className="mt-2 text-center text-2xl font-bold uppercase tracking-[0.16em] text-slate-900">Parecer Tecnico do Gestor da Parceria</h1>

          <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-4 print:grid-cols-4">
            <Field label="Plano de Trabalho" value={plano.titulo || 'Nao informado'} className="sm:col-span-2 print:col-span-2" />
            <Field label="Protocolo" value={plano.numeroProtocolo || 'Sem protocolo'} />
            <Field label="Modalidade" value={plano.modalidade || 'Nao informada'} />
            <Field label="Organizacao da Sociedade Civil" value={plano.instituicaoNome || 'Nao informada'} className="sm:col-span-2 print:col-span-2" />
            <Field label="Gestor da Parceria" value={plano.parecerTecnicoInseridoPor || plano.gestorNome || 'Nao informado'} />
            <Field label="Secretaria" value={plano.gestorSecretaria || 'Nao informada'} />
          </div>
        </header>

        <SectionTitle number="1" title="Objeto da Parceria" />
        <p className="whitespace-pre-wrap text-sm leading-6 text-slate-800">
          {plano.objetoParceria || 'Nao informado.'}
        </p>

        <SectionTitle number="2" title="Parecer Tecnico" />
        {semParecer ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Nenhum parecer tecnico foi registrado ainda. Salve o parecer antes de imprimir.
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-6 text-slate-900">{plano.parecerTecnico}</p>
        )}

        <SectionTitle number="3" title="Autenticidade" />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 print:grid-cols-2">
          <Field label="Registrado por" value={plano.parecerTecnicoInseridoPor || plano.gestorNome || 'Nao informado'} />
          <Field label="Registrado em" value={inseridoEm} />
          <Field
            label="Hash SHA-256"
            value={<span className="break-all font-mono text-[11px]">{plano.parecerTecnicoHash || 'Nao informado'}</span>}
            className="sm:col-span-2 print:col-span-2"
          />
        </div>

        <div className="mt-16 flex justify-center print-keep-together">
          <div className="w-80 border-t border-slate-400 pt-2 text-center text-sm text-slate-700">
            {plano.parecerTecnicoInseridoPor || plano.gestorNome || 'Gestor da Parceria'}
            <span className="block text-xs text-slate-500">Gestor da Parceria</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ParecerGestorPrintPage;
